import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "./PhotographerDetail.css";

function splitTopLevelPublicationSegment(text) {
  const entries = [];
  let current = "";
  let depth = 0;
  const isBracketOpen = (ch) => "([{（【".includes(ch);
  const isBracketClose = (ch) => ")]}）】".includes(ch);

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const prev = text[i - 1] || "";
    const next = text[i + 1] || "";

    if (isBracketOpen(ch)) {
      depth += 1;
    } else if (isBracketClose(ch)) {
      depth = Math.max(0, depth - 1);
    }

    const isTopLevelSemicolon = (ch === ";" || ch === "；") && depth === 0;
    const isTopLevelDotBullet =
      (ch === "·" || ch === "•") && depth === 0 && /\s/.test(prev) && /\s/.test(next);

    if (isTopLevelSemicolon || isTopLevelDotBullet) {
      const trimmed = current.trim();
      if (trimmed) entries.push(trimmed);
      current = "";
      continue;
    }

    current += ch;
  }

  const tail = current.trim();
  if (tail) entries.push(tail);
  return entries;
}

function parsePublicationEntries(publicationText) {
  if (!publicationText || typeof publicationText !== "string") return [];

  const normalized = publicationText.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);

  const entries = [];
  paragraphs.forEach((paragraph) => {
    splitTopLevelPublicationSegment(paragraph).forEach((item) => {
      if (item) entries.push(item);
    });
  });

  return entries;
}

function getExternalHref(url) {
  if (!url || typeof url !== "string") return "";

  const trimmed = url.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function PhotographerDetail({ language = "EN" }) {
  const { id } = useParams();
  const [photographer, setPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageClass = `photographer-detail-page ${
    language === "CN" ? "photographer-cn" : ""
  }`;

  useEffect(() => {
    const fetchPhotographerDetail = async () => {
      setLoading(true);
      try {
        // 现行实现：直接读取静态JSON（public/data/photographers.json）
        const res = await fetch("/data/photographers.json", {
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log(
          `✅ Photographer detail loaded from static JSON (total ${
            data?.length || 0
          })`
        );

        // 根据ID查找特定摄影师
        const photographerData = Array.isArray(data)
          ? data.find((p) => p.photographerid === parseInt(id, 10))
          : null;

        if (photographerData) {
          setPhotographer(photographerData);
          setError(null);
        } else {
          setError(
            language === "CN"
              ? `未找到 ID 为 ${id} 的摄影师`
              : `Photographer with ID ${id} not found`
          );
        }
      } catch (err) {
        console.error("Error loading photographer detail:", err);
        setError(
          language === "CN"
            ? `加载摄影师详情失败：${err.message}`
            : `Failed to load photographer detail: ${err.message}`
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPhotographerDetail();
    }
  }, [id, language]);

  if (loading) {
    return (
      <div className={pageClass}>
        <div className="loading-container">
          <div className="loading-animation">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
            <p className="loading-text">
              {language === "CN"
                ? "正在加载摄影师详情..."
                : "Loading photographer..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageClass}>
        <div className="error-container">
          <h2>{language === "CN" ? "错误" : "Error"}</h2>
          <p>{error}</p>
          <Link to="/photographers" className="back-link">
            {language === "CN" ? "返回摄影师列表" : "Back to Photographers"}
          </Link>
        </div>
      </div>
    );
  }

  if (!photographer) {
    return (
      <div className={pageClass}>
        <div className="not-found-container">
          <h2>{language === "CN" ? "未找到摄影师" : "Photographer Not Found"}</h2>
          <p>
            {language === "CN"
              ? "你查看的摄影师不存在或已被移除。"
              : "The photographer you're looking for doesn't exist or has been removed."}
          </p>
          <Link to="/photographers" className="back-link">
            {language === "CN" ? "返回摄影师列表" : "Back to Photographers"}
          </Link>
        </div>
      </div>
    );
  }

  const nation =
    language === "CN"
      ? photographer.nation_zh || photographer.nation || "未知"
      : photographer.nation || "Unknown";
  const birthyear = photographer.birthyear || (language === "CN" ? "未知" : "Unknown");
  const biography =
    language === "CN"
      ? photographer.biography_zh || photographer.biography
      : photographer.biography;
  const publication =
    language === "CN"
      ? photographer.publication_zh || photographer.publication
      : photographer.publication;
  const publicationEntries = parsePublicationEntries(publication);
  const websiteHref = getExternalHref(photographer.website);

  return (
    <div className={pageClass}>
      <div className="photographer-header">
        <h1 className="photographer-name">{photographer.name}</h1>
        <h2 className="photographer-subtitle">
          {language === "CN"
            ? `${nation}，出生于 ${birthyear}`
            : `${nation}, born ${birthyear}`}
        </h2>
      </div>

      <div className="photographer-content">
        <div className="photographer-sections">
          {biography && (
            <div className="biography-section">
              <h3>{language === "CN" ? "生平" : "Biography"}</h3>
              <p>{biography}</p>
            </div>
          )}

          <div className="links-section">
            {photographer.website && (
              <div className="website-section">
                <h3>{language === "CN" ? "网站" : "Website"}</h3>
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  {photographer.website}
                </a>
              </div>
            )}

            <div className="publications-section">
              <h3>{language === "CN" ? "出版物" : "Publications"}</h3>
              {publicationEntries.length > 0 ? (
                <ul className="publications-list">
                  {publicationEntries.map((entry, index) => (
                    <li key={`${photographer.photographerid}-pub-${index}`}>
                      {entry}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-content">
                  {language === "CN"
                    ? "暂无出版物记录"
                    : "No publications recorded"}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="back-navigation">
          <Link to="/photographers" className="back-link">
            {language === "CN"
              ? "← 返回全部摄影师"
              : "← Back to all photographers"}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PhotographerDetail;
